import {
  ClinicalCodeListReadDto,
  ClinicalCommentReadDto,
  ClinicalDatasetReadDto,
  ClinicalLibraryReadDto,
  ClinicalMethodReadDto,
  ClinicalValueListReadDto,
  ClinicalWhereClauseReadDto,
} from "functions/api/library/interface";

export interface ClinicalLibraryTemplates {
  clinicalDatasets: ClinicalDatasetReadDto[];
  clinicalCodeLists: ClinicalCodeListReadDto[];
  clinicalValueLists: ClinicalValueListReadDto[];
  clinicalComments: ClinicalCommentReadDto[];
  clinicalMethods: ClinicalMethodReadDto[];
  clinicalWhereClauses: ClinicalWhereClauseReadDto[];
}

/**
 * Merges clinical library data into an accumulator, avoiding duplicates based on OID.
 *
 * @param acc - The accumulator containing existing clinical library data
 * @param clinicalLibrary - The clinical library to merge
 * @returns Updated accumulator with merged clinical library data
 */
export const mergeClinicalLibraryData = (
  acc: ClinicalLibraryTemplates,
  clinicalLibrary: ClinicalLibraryReadDto,
): ClinicalLibraryTemplates => {
  // Add clinical datasets
  const clinicalDatasets = [
    ...acc.clinicalDatasets,
    ...clinicalLibrary.clinicalDatasets.filter(
      (dataset: ClinicalDatasetReadDto) => !acc.clinicalDatasets.some((d) => d.oid === dataset.oid),
    ),
  ];

  // Add clinical code lists
  const clinicalCodeLists = [
    ...acc.clinicalCodeLists,
    ...clinicalLibrary.clinicalCodeLists.filter(
      (codeList: ClinicalCodeListReadDto) =>
        !acc.clinicalCodeLists.some((cl) => cl.oid === codeList.oid),
    ),
  ];

  // Add clinical value lists
  const clinicalValueLists = [
    ...acc.clinicalValueLists,
    ...clinicalLibrary.clinicalValueLists.filter(
      (valueList: ClinicalValueListReadDto) =>
        !acc.clinicalValueLists.some((vl) => vl.oid === valueList.oid),
    ),
  ];

  // Add clinical comments
  const clinicalComments = [
    ...acc.clinicalComments,
    ...clinicalLibrary.clinicalComments.filter(
      (comment: ClinicalCommentReadDto) => !acc.clinicalComments.some((c) => c.oid === comment.oid),
    ),
  ];

  // Add clinical methods
  const clinicalMethods = [
    ...acc.clinicalMethods,
    ...clinicalLibrary.clinicalMethods.filter(
      (method: ClinicalMethodReadDto) => !acc.clinicalMethods.some((m) => m.oid === method.oid),
    ),
  ];

  // Add clinical where clauses
  const clinicalWhereClauses = [
    ...acc.clinicalWhereClauses,
    ...clinicalLibrary.clinicalWhereClauses.filter(
      (whereClause: ClinicalWhereClauseReadDto) =>
        !acc.clinicalWhereClauses.some((wc) => wc.oid === whereClause.oid),
    ),
  ];

  return {
    clinicalDatasets,
    clinicalCodeLists,
    clinicalValueLists,
    clinicalComments,
    clinicalMethods,
    clinicalWhereClauses,
  };
};
